import { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import { House, UsersThree, Tag, ShoppingCart, Package, Truck, CurrencyCircleDollar, ClipboardText, GearSix, ChartBar, Question, List, X, SignOut, CalendarBlank, Plus, Minus } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { TrialBanner } from "@/components/TrialBanner";
import AiChat from "@/components/AiChat";
import { api, fmtDate, todayStr } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import { applyTheme } from "@/lib/theme";

export const NAV = [
  { to: "/", label: "Ana Sayfa", icon: House, id: "dashboard", end: true },
  { to: "/cariler", label: "Müşteri & Tedarikçi", icon: UsersThree, id: "accounts", children: [
    ["/cariler", "Tüm Cariler"], ["/cariler?tip=customer", "Müşteriler"], ["/cariler?tip=supplier", "Tedarikçiler"], ["/cari-iletisim", "Eksik İletişim"], ["/mutabakatlar", "Mutabakatlar"]] },
  { to: "/stok", label: "Ürünler", icon: Tag, id: "products", children: [
    ["/stok", "Ürün Listesi"], ["/stok?filtre=kritik", "Kritik Stok"], ["/raporlar?tip=stock-movements", "Stok Hareketleri"]] },
  { to: "/siparisler", label: "Siparişler", icon: ClipboardText, id: "orders", children: [
    ["/siparisler", "Tüm Siparişler"], ["/siparisler?tur=sale", "Satış Siparişleri"], ["/siparisler?tur=purchase", "Alış Siparişleri"], ["/siparisler/yeni?tur=sale", "Yeni Sipariş"]] },
  { to: "/faturalar?tur=purchase", label: "Alışlar", icon: Truck, id: "purchases", children: [
    ["/faturalar?tur=purchase", "Alış Faturaları"], ["/faturalar/yeni?tur=purchase", "Yeni Alış Faturası"]] },
  { to: "/faturalar?tur=sale", label: "Satışlar", icon: ShoppingCart, id: "sales", children: [
    ["/faturalar?tur=sale", "Satış Faturaları"], ["/faturalar/yeni?tur=sale", "Yeni Satış Faturası"]] },
  { to: "/sevkiyatlar", label: "Sevkiyatlar", icon: Package, id: "shipments", children: [
    ["/sevkiyatlar", "Sevk Bekleyenler"], ["/sevkiyatlar?tur=sale", "Sevkler"], ["/sevkiyatlar?tur=purchase", "Mal Kabuller"], ["/raporlar?tip=shipments", "Sevk Raporu"]] },
  { to: "/kasa", label: "Nakit Yönetimi", icon: CurrencyCircleDollar, id: "cash", children: [
    ["/kasa", "Kasa / Tahsilat"], ["/kasa?tur=collection", "Tahsilatlar"], ["/kasa?tur=payment", "Ödemeler"], ["/cek-senet", "Çek / Senet"], ["/hatirlatmalar", "Vade Hatırlatma"]] },
  { to: "/ayarlar", label: "Ayarlar", icon: GearSix, id: "settings", children: [
    ["/ayarlar", "Firma Bilgileri"], ["/plan", "Planım"], ["/cari-iletisim", "Eksik İletişim"], ["/islem-gecmisi", "İşlem Geçmişi"], ["/rehber", "Nasıl Yapılır?"], ["/sirketler", "Müşteri Şirketleri"]] },
  { to: "/raporlar", label: "Raporlar", icon: ChartBar, id: "reports", children: [
    ["/raporlar?tip=sales", "Satış Raporu"], ["/raporlar?tip=purchases", "Alış Raporu"], ["/raporlar?tip=accounts", "Cari Bakiye"],
    ["/raporlar?tip=stock", "Stok Durumu"], ["/raporlar?tip=cash", "Kasa Raporu"], ["/raporlar?tip=vat", "KDV Raporu"]] },
  { to: "/yardim", label: "Yardım", icon: Question, id: "help" },
];

const MODULE_OF = { accounts: "accounts", products: "products", orders: "orders", purchases: "invoices", sales: "invoices", shipments: "shipments", cash: "cash", reports: "reports" };
export const visibleNav = (user) => NAV.filter((n) => !MODULE_OF[n.id] || !user?.perms || user.perms[MODULE_OF[n.id]] !== "none")
  .map((n) => n.children ? { ...n, children: n.children.filter(([to]) => (to !== "/islem-gecmisi" || ["owner", "admin"].includes(user?.role)) && (to !== "/sirketler" || (user?.role === "owner" && user?.tenant?.platform_admin))) } : n);

const isActiveFor = (item, loc) => {
  const [path, qs] = item.to.split("?");
  if (item.end) return loc.pathname === "/";
  if (!loc.pathname.startsWith(path)) return false;
  if (!qs) return true;
  const [k, v] = qs.split("=");
  return new URLSearchParams(loc.search).get(k) === v;
};

function NavItem({ item, collapsed, loc, onNavigate }) {
  const active = isActiveFor(item, loc);
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);
  const Icon = item.icon;
  const cls = `group mx-2 my-1 flex w-[calc(100%-1rem)] items-center gap-3 rounded-md px-4 py-2.5 text-[15px] transition-[background-color,box-shadow,transform] duration-150 ${active ? "nav-emboss-active bg-black/30 text-white" : "nav-emboss bg-white/[0.06] text-slate-200 hover:bg-white/10 hover:text-white active:translate-y-px"}`;
  const inner = (<>
    <Icon size={20} weight="fill" className="shrink-0 text-slate-100" />
    {!collapsed && <span className="truncate">{item.label}</span>}
    {!collapsed && item.badge && <span className="rounded-full bg-lime-300 px-2 py-0.5 text-[11px] font-bold text-slate-900" data-testid={`nav-badge-${item.id}`}>{item.badge}</span>}
    {!collapsed && item.children && <span className="ml-auto text-slate-300">{open ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}</span>}
  </>);
  if (item.external) return <a href={item.to} className={cls} data-testid={`nav-${item.id}`} title={item.label}>{inner}</a>;
  if (item.children && !collapsed) {
    return (
      <div>
        <button type="button" onClick={() => setOpen((o) => !o)} className={cls} data-testid={`nav-${item.id}`} aria-expanded={open}>{inner}</button>
        <div className={`mx-2 overflow-hidden rounded-md bg-black/20 transition-[max-height] duration-200 ${open ? "max-h-[28rem]" : "max-h-0"}`} data-testid={`nav-sub-${item.id}`}>
          {item.children.map(([to, label]) => {
            const sub = isActiveFor({ to }, loc) && (to.includes("?") || loc.search === "" || !item.children.some(([t]) => t !== to && isActiveFor({ to: t }, loc)));
            return (
              <Link key={to} to={to} onClick={onNavigate} data-testid={`nav-sub-${item.id}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`block py-2 pl-14 pr-4 text-sm transition-colors ${sub ? "text-[var(--nav-active,#4fd1c5)]" : "text-slate-300 hover:text-white"}`}>{label}</Link>
            );
          })}
        </div>
      </div>
    );
  }
  return <NavLink to={item.to} onClick={onNavigate} className={cls} data-testid={`nav-${item.id}`} title={item.label}>{inner}</NavLink>;
}

function Sidebar({ open, collapsed, onClose }) {
  const loc = useLocation();
  const { user, logout } = useAuth();
  const items = visibleNav(user);
  const w = collapsed ? "lg:w-[72px]" : "lg:w-64";
  return (
    <aside data-testid="sidebar" className={`no-print fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-[var(--sidebar-bg,#0f2033)] text-slate-200 transition-all duration-200 lg:translate-x-0 ${w} ${open ? "translate-x-0" : "-translate-x-full"}`}>
      <div className={`flex h-16 items-center border-b border-white/10 ${collapsed ? "justify-center" : "justify-between px-5"}`}>
        <Link to="/" className="flex items-center" data-testid="sidebar-brand">
          {collapsed ? <img src="/favicon.png" alt="" className="h-9 w-9 rounded" /> : <img src="/logo-r-light.png" alt="BİZİMCARİ" className="h-8 w-auto" />}
        </Link>
        <button className="lg:hidden" onClick={onClose} data-testid="sidebar-close-button"><X size={20} /></button>
      </div>
      <nav className="flex-1 overflow-y-auto py-2" data-testid="sidebar-nav">
        {items.map((item) => <NavItem key={item.id} item={item} collapsed={collapsed} loc={loc} onNavigate={onClose} />)}
      </nav>
      <div className="border-t border-white/10 p-2" data-testid="sidebar-account">
        {!collapsed && <div className="truncate px-3 pb-1 text-xs uppercase tracking-wide text-slate-300" data-testid="sidebar-user-name" title={user?.email}>{user?.name || user?.email}</div>}
        <button onClick={logout} className={`nav-emboss flex w-full items-center gap-3 rounded-md bg-white/[0.06] px-4 py-2.5 text-[15px] text-slate-200 hover:bg-white/10 hover:text-white ${collapsed ? "justify-center" : ""}`} data-testid="logout-button" title="Çıkış Yap">
          <SignOut size={20} weight="fill" />{!collapsed && <span>Çıkış Yap</span>}
        </button>
      </div>
    </aside>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("bc_sidebar") === "1");
  const [company] = useLoad(() => api.get("/company").then((r) => r.data), []);
  useEffect(() => { api.get("/settings/theme").then((r) => applyTheme(r.data.theme)).catch(() => applyTheme("turkuaz")); }, []);
  const toggle = () => {
    if (window.innerWidth < 1024) return setOpen(true);
    setCollapsed((c) => { localStorage.setItem("bc_sidebar", c ? "0" : "1"); return !c; });
  };
  const dayNum = todayStr().slice(8, 10);
  return (
    <div className="min-h-screen bg-[var(--app-bg,#e6f7f5)]">
      <Sidebar open={open} collapsed={collapsed} onClose={() => setOpen(false)} />
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className={`print-main transition-[padding] duration-200 ${collapsed ? "lg:pl-[72px]" : "lg:pl-64"}`}>
        <header className="no-print sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-teal-100 bg-white/90 px-4 shadow-[0_2px_8px_rgba(13,148,136,0.12)] backdrop-blur sm:px-6">
          <button onClick={toggle} data-testid="sidebar-toggle-button" className="text-slate-700 hover:text-slate-900"><List size={26} /></button>
          <span className="truncate text-sm font-bold uppercase tracking-wide text-slate-700" data-testid="header-company-name">{company?.name || company?.title || "Bizim Cari"}</span>
          <div className="ml-auto flex items-center gap-5">
            <Link to="/hatirlatmalar" className="relative text-slate-700 hover:text-slate-900" data-testid="header-today" title={fmtDate(todayStr())}>
              <CalendarBlank size={30} />
              <span className="absolute -right-2 -top-1 rounded bg-red-600 px-1 text-[10px] font-bold text-white">{dayNum}</span>
            </Link>
          </div>
        </header>
        <TrialBanner />
        {user?.perms?.reports !== "none" && <AiChat />}
        <main className="mx-auto max-w-[1500px] px-4 py-6 sm:px-6 print-main">
          <Outlet />
        </main>
        <footer className="no-print mx-auto flex max-w-[1500px] items-center justify-between px-4 pb-6 text-[11px] text-slate-400 sm:px-6" data-testid="app-footer">
          <img src="/logo-copyright.png" alt="© BİZİMCARİ 2026" className="h-7 w-auto opacity-70" />
          <span>Tüm hakları saklıdır</span>
        </footer>
      </div>
    </div>
  );
}
